import { takeEvery, put, select } from "redux-saga/effects";
import api from "../../../../utils/apiService";
import { ENDPOINT } from "../../../../config/endpoints";
import {
  getBizBrandsData, getBizBrandsDataFail, getBizBrandsDataSuccess, getCatalogueData,
  getCatalogueDataSuccess, getDiscountsData, getDiscountsDataSuccess, getItemTag,
  getItemTagSuccess, getOptionSetsData, getOptionSetsDataSuccess, getPOSSettingsData,
  getPOSSettingsDataSuccess, storeItemTagsPriority, saveMostSoldItems, getMostSoldItems,
  posSettingsCallComplete, catalogueDataCallComplete, optionSetsDataCallComplete,
  mostSoldItemsCallComplete, discountsDataCallComplete, bizBrandsDataCallComplete,
  itemTagCallComplete,
} from "../../../slices/catalogueSlice";
import { getItemsData, getItemsDataSuccess, itemsDataCallComplete } from "../../../slices/ItemsSlice";
import { pollForTickets } from "../../../../effects-bridge";
import { saveOptionSetsOffline, putItemsOffline, saveOthersOffline } from "../../../../helpers/OfflineHelper";
import { arrayToMap, setPriorityForItemTags } from "../../../../helpers/catalogueHelpers";
import { othersDataTypes } from "../../../../facade/ServiceFacade";
import { MID } from "../../../../utils/constants";
import { METHODS } from "../../../../data/constants";
import { isArray } from "lodash";

export * from "./shared";

/**
 * WEB variant of `redux-store/saga/pos/catalog`. See `./shared.ts` for why nothing is shared.
 *
 * ⚠️ `pollForTickets` comes from the EFFECTS BRIDGE (user decision, 2026-08-23). Behind it sits
 * a 900-line poller that imports `helper/printHelper` — printer hardware core cannot own.
 *
 * ⚠️ Dropped two DEAD imports the app file carried, `get` (`utils/OfflineUtil`) and `callWorker`
 * (`utils/myworker`): each appeared exactly once, on its own import line, never in the body.
 *
 * ⚠️ `getCatalog` writes every `othersDataTypes` section to the offline store after a successful
 * fetch. That loop and this file's `othersDataTypes` import are the reason `facade/ServiceFacade`
 * had to keep re-exporting the table from the app — the app's own saga still consumed it.
 */

function* getCatalog(data: any): any {
  try {
    const response = yield api.request({
      method: METHODS.GET,
      url: ENDPOINT.POS.getCatalog(),
      hideLoader: data?.payload,
    } as any);
    if(!response?.code ) {
      yield put(getCatalogueDataSuccess(response));
      for (const typeObj of othersDataTypes) {
        if (response[typeObj.key]?.length) {
          yield saveOthersOffline({
            type: typeObj.type,
            items: response[typeObj.key],
          });
        }
      }

    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(catalogueDataCallComplete());
  }
}
function* getItems(data: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.POS.getItems(),
      hideLoader: data?.payload
    } as any)
    console.log("response items: ", response);
    yield put(getItemsDataSuccess(response));
    yield putItemsOffline(response);
  } catch (error) {
    console.log('getItems--err', error);
  } finally {
    yield put(itemsDataCallComplete());
  }
}
function* getOptionSets(data: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.POS.getOptionSets(),
      hideLoader: data?.payload
    } as any)
    yield put(getOptionSetsDataSuccess({response}));
    yield saveOptionSetsOffline(response);
  } catch (error) {
    console.log(error);
  } finally {
    yield put(optionSetsDataCallComplete());
  }
}

function* getDiscounts(data: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.POS.getDiscounts(),
      hideLoader: data?.payload
    } as any)

    yield put(getDiscountsDataSuccess({ response }));
    yield saveOthersOffline({
      type: 'discount',
      items: response
    });
  } catch (error) {
    console.log(error);
  } finally {
    yield put(discountsDataCallComplete());
  }
}


function* getPOSSettings(action: any): any {
  try {
    const response = yield api.request({
      method: METHODS.GET,
      url: `${ENDPOINT.POS.getPOSSettings()}/${action.payload.branchId}`,
      skipGlobalLoader: action?.payload?.skipGlobalLoader,
    } as any);
    const posSettingsFromLS = yield select(state => state.catalogStore?.posSettings);
    if (
      !response?.code &&
      (!posSettingsFromLS?.version ||
        posSettingsFromLS?.version !== response?.version)
    ) {
      yield put(getPOSSettingsDataSuccess({ response }));
      // pollForTickets();
    }
    // yield put(getPOSSettingsDataSuccess({ response }));
    pollForTickets()
    // callWorker()
    action.payload.callback && action.payload.callback();
  } catch (error) {
    console.log(error);
    action.payload.callback && action.payload.callback();
  } finally {
    yield put(posSettingsCallComplete());
  }
}

function* fetchMostSoldItems(action: any): any {
  try {
    const response = yield api.request({
      method: `POST`,
      url: ENDPOINT.POS.getMostSoldItems(),
      data: action.payload
    } as any);
    if (!response?.code && isArray(response)) {
      yield put(saveMostSoldItems(response));
    }
  } catch (error) {
    console.log(error);
  } finally {
    yield put(mostSoldItemsCallComplete());
  }
}

function* getBizBrands(data: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: `${ENDPOINT.POS.getBizBrands()}`,
      hideLoader: data?.payload
    } as any);
    yield put(getBizBrandsDataSuccess({ response }));
  } catch (error) {
    yield put(getBizBrandsDataFail());
  } finally {
    yield put(bizBrandsDataCallComplete());
  }
}

function* getItemTags(data: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: `${ENDPOINT.POS.getItemTag()}`,
      hideLoader: data?.payload
    } as any);
    console.log("check here item tags---------->", response);
    if(!!response?.length) {
      let tagsData = arrayToMap(response, MID)
      let tagsPriorityArr = setPriorityForItemTags(tagsData);
      yield put(storeItemTagsPriority(tagsPriorityArr));
    }
    yield put(getItemTagSuccess({ response }));
  } catch (error) {
    console.log(error);
  } finally {
    yield put(itemTagCallComplete());
  }
}

export default function* root(): any {
  yield takeEvery(getCatalogueData.type, getCatalog);
  yield takeEvery(getItemsData.type, getItems);
  yield takeEvery(getOptionSetsData.type, getOptionSets);
  yield takeEvery(getPOSSettingsData.type, getPOSSettings);
  yield takeEvery(getDiscountsData.type, getDiscounts);
  yield takeEvery(getMostSoldItems.type, fetchMostSoldItems);
  yield takeEvery(getBizBrandsData.type, getBizBrands);
  yield takeEvery(getItemTag.type, getItemTags);
}
